// src/components/ImageUploader/ImageUploader.jsx
import { useEffect, useRef, useState } from "react";
import styles from "./ImageUploader.module.scss";
import api from "@/utils/axios";

/** Pick the lowest-res srcset URL for preview; fallback to poster */
function pickLowResPreview(img) {
  const list = Array.isArray(img?.srcset) ? img.srcset : [];
  if (list.length > 0) {
    const smallest = [...list].sort((a, b) => (a?.w || 0) - (b?.w || 0))[0];
    if (smallest?.url) return smallest.url;
  }
  return img?.poster || null;
}

export default function ImageUploader({
  label = "Image",
  slot = "desktopPrimary",
  initial = null,

  // Endpoints
  createJobPath = "/v1/homeCMS/images/create-job", // POST to create job
  refreshDocPath = null, // ⛔ default OFF (no periodic refresh)
  autoRefresh = false, // ⛔ default OFF (enable to keep signed URLs warm)
  selectSlot = (doc, slot) => (doc?.home || doc)?.images?.[slot], // how to pull slot from response

  // Upload config
  accept = "image/png,image/jpeg,image/webp",
  maxBytes = 15 * 1024 * 1024,
  token = "",
  partSize = 5 * 1024 * 1024,

  // Optional override for SSE base
  sseBase,

  // Callback (fires only when THIS uploader's job reaches "ready")
  onReady,
}) {
  const API =
    sseBase ||
    (typeof import.meta !== "undefined" &&
      import.meta.env?.VITE_API_BASE_URL) ||
    (typeof window !== "undefined" ? `${window.location.origin}/api` : "/api");

  // Renew signed URLs before expiry (~5min TTL on server)
  const REFRESH_MS =
    (typeof import.meta !== "undefined" &&
      Number(import.meta.env?.VITE_CDN_URL_REFRESH_MS)) ||
    240_000;

  // state
  const [current, setCurrent] = useState(initial);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(pickLowResPreview(initial));
  const [progress, setProgress] = useState(0);
  const [ok, setOk] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  // refs
  const esRef = useRef(null);
  const revokeRef = useRef(null);
  const abortRef = useRef(null);
  const intervalRef = useRef(null);
  const jobIdRef = useRef(null); // ownership: the job started by THIS uploader
  const lastReadySigRef = useRef(null); // dedupe signature for final onReady

  /* Cleanup on unmount */
  useEffect(() => {
    return () => {
      if (esRef.current) {
        try {
          esRef.current.close();
        } catch {
          // Intentionally ignored: cleanup failure should not block the UI.
        }
      }
      if (abortRef.current) {
        try {
          abortRef.current.abort();
        } catch {
          // Intentionally ignored: cleanup failure should not block the UI.
        }
      }
      if (revokeRef.current) {
        try {
          URL.revokeObjectURL(revokeRef.current);
        } catch {
          // Intentionally ignored: cleanup failure should not block the UI.
        }
      }
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const initialSrcsetSignature = (initial?.srcset || [])
    .map((s) => `${s.w}:${s.url}`)
    .join("|");

  /* Sync state when parent "initial" changes (but don't clobber a selected file) */
  useEffect(() => {
    if (file) return; // don't override local selection during an upload

    const nextPreview = pickLowResPreview(initial);
    if (nextPreview) {
      setCurrent(initial);
      setPreviewUrl(nextPreview);
    }
  }, [file, initial, initialSrcsetSignature, slot]);

  /* Optional: auto-refresh signed URLs before expiry (never calls onReady) */
  useEffect(() => {
    if (!autoRefresh || !refreshDocPath || !current?.poster) return;

    if (intervalRef.current) clearInterval(intervalRef.current);

    const tick = async () => {
      if (file) return; // don't fight an active upload
      try {
        const { data } = await api.get(refreshDocPath);
        const img = selectSlot(data, slot);
        const nextPreview = pickLowResPreview(img);
        // update preview URLs silently (no onReady here)
        if (img?.poster && nextPreview && nextPreview !== previewUrl) {
          setCurrent(img);
          setPreviewUrl(nextPreview);
        }
      } catch {
        /* silent */
      }
    };

    // If you want an immediate refresh once, uncomment next line:
    // tick();

    intervalRef.current = setInterval(tick, Math.max(60_000, REFRESH_MS));

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [
    autoRefresh,
    refreshDocPath,
    current?.poster,
    slot,
    file,
    previewUrl,
    REFRESH_MS,
    selectSlot,
  ]);

  function getContentType(f) {
    return f?.type || "application/octet-stream";
  }

  function onChange(e) {
    setErr("");
    const f = e.target.files?.[0] || null;
    setFile(f);
    setOk(false);
    setProgress(0);

    if (revokeRef.current) {
      try {
        URL.revokeObjectURL(revokeRef.current);
      } catch {
        // Intentionally ignored: cleanup failure should not block the UI.
      }
      revokeRef.current = null;
    }

    const url = f ? URL.createObjectURL(f) : pickLowResPreview(current);
    if (f) revokeRef.current = url;
    setPreviewUrl(url);
  }

  function onClear() {
    setErr("");
    if (revokeRef.current) {
      try {
        URL.revokeObjectURL(revokeRef.current);
      } catch {
        // Intentionally ignored: cleanup failure should not block the UI.
      }
      revokeRef.current = null;
    }
    setFile(null);
    setPreviewUrl(pickLowResPreview(current));
    setProgress(0);
    setOk(false);
  }

  async function upload() {
    try {
      setErr("");
      setMsg("");
      setOk(false);
      setProgress(0);

      if (!file) return setErr("Please pick a file.");
      if (file.size > maxBytes) {
        return setErr(`Max size is ${(maxBytes / 1024 / 1024).toFixed(0)} MB.`);
      }

      // 1) Create job
      let ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      if (ext === "heic") ext = "jpg";
      const { data: job } = await api.post(createJobPath, { slot, ext });
      const { jobId, originalKey } = job;
      jobIdRef.current = jobId; // mark ownership

      // 2) Initiate multipart
      const { data: init } = await api.post("/v1/uploads/presign/initiate", {
        key: originalKey,
        contentType: getContentType(file),
      });
      const { uploadId } = init;

      // 3–4) Upload parts
      const parts = [];
      const totalParts = Math.ceil(file.size / partSize);
      const ac = new AbortController();
      abortRef.current = ac;

      for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
        const start = (partNumber - 1) * partSize;
        const end = Math.min(start + partSize, file.size);
        const blob = file.slice(start, end);

        const { data: partSig } = await api.post("/v1/uploads/presign/part", {
          key: originalKey,
          uploadId,
          partNumber,
        });

        const putRes = await fetch(partSig.url, {
          method: "PUT",
          body: blob,
          signal: ac.signal,
        });

        if (!putRes.ok) throw new Error(`Upload part ${partNumber} failed`);
        const etag = putRes.headers.get("ETag") || "";
        parts.push({ ETag: etag, PartNumber: partNumber });

        setProgress(Math.round((partNumber / totalParts) * 90));
      }

      // 5) Complete multipart
      await api.post("/v1/uploads/presign/complete", {
        jobId,
        key: originalKey,
        uploadId,
        parts,
      });

      setProgress(92);
      setMsg("Uploaded. Processing…");

      // 6) SSE: wait for ready
      if (esRef.current) {
        try {
          esRef.current.close();
        } catch {
          // Intentionally ignored: cleanup failure should not block the UI.
        }
        esRef.current = null;
      }

      const sseToken =
        token ||
        (typeof localStorage !== "undefined"
          ? localStorage.getItem("cm_at") || ""
          : "");
      const esUrl = `${API}/v1/uploads/subscribe?jobId=${encodeURIComponent(
        jobId,
      )}${sseToken ? `&access_token=${encodeURIComponent(sseToken)}` : ""}`;

      const es = new EventSource(esUrl);
      esRef.current = es;

      es.addEventListener("update", async (evt) => {
        const data = JSON.parse(evt.data || "{}");
        if (data.type === "processing") setProgress(96);

        if (data.type === "ready") {
          const iOwnThis = !!jobIdRef.current;

          setProgress(100);
          setOk(true);
          setMsg("Ready & published!");

          try {
            // If refreshDocPath is not provided, try to derive `/v1/homeCMS` from createJobPath
            const docPath =
              refreshDocPath ||
              createJobPath.replace(/\/images\/create-job$/, "");

            if (docPath) {
              const { data: fresh } = await api.get(docPath);
              const img = selectSlot(fresh, slot);
              if (img?.poster) {
                setCurrent(img);
                setPreviewUrl(pickLowResPreview(img));

                if (iOwnThis && onReady) {
                  const sig =
                    img?.updatedAt || img?.poster || JSON.stringify(img);
                  if (sig && sig !== lastReadySigRef.current) {
                    lastReadySigRef.current = sig;
                    onReady(img);
                  }
                }
              }
            }
          } catch {
            // ignore fetch failure; image may still be visible via cached URL
          }

          try {
            es.close();
          } catch {
            // Intentionally ignored: cleanup failure should not block the UI.
          }
          esRef.current = null;
          jobIdRef.current = null; // release ownership
        }
      });

      es.onerror = () => {
        // keep silent; EventSource will auto-retry if needed until 'ready'
      };
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || e?.message || "Upload failed");
    }
  }

  return (
    <div className={styles.filePicker}>
      <label className={styles.label}>
        {label}
        <input type="file" accept={accept} onChange={onChange} />
      </label>

      <div className={styles.row}>
        <StatusBadge ok={ok} />
        {file && (
          <span className={styles.fileInfo}>
            {file.name} • {(file.size / 1024 / 1024).toFixed(2)} MB
          </span>
        )}
        {file && (
          <button
            type="button"
            className={`nu-btn nu-btn--xs ${styles.clearBtn}`}
            onClick={onClear}
          >
            Clear
          </button>
        )}
        <button
          type="button"
          className={`nu-btn ${styles.uploadBtn}`}
          onClick={upload}
        >
          {progress > 0 && progress < 100
            ? `Uploading… ${progress}%`
            : "Upload"}
        </button>
      </div>

      <div className={styles.previewWrap}>
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={`${label} preview`}
            className={styles.previewImg}
            loading="lazy"
          />
        ) : (
          <span className={styles.previewPlaceholder}>No image</span>
        )}
      </div>

      {msg && <div className={styles.message}>{msg}</div>}
      {err && <div className={styles.err}>{err}</div>}
    </div>
  );
}

function StatusBadge({ ok }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "4px 8px",
        borderRadius: 8,
        marginLeft: 8,
        background: ok ? "rgba(0, 190, 120, 0.15)" : "rgba(0,0,0,0.06)",
        color: ok ? "#07a36c" : "#666",
        fontSize: 12,
      }}
    >
      {ok ? "Ready" : "Pending"}
    </span>
  );
}
